import $ from 'jquery';
import apiFetch from "@wordpress/api-fetch";
import actions from '@wc-stripe/actions';
import cart from '@wc-stripe/cart';
import {loadStripeSDK} from '@wc-stripe/stripe-sdk';
import CheckoutFields from '@wc-stripe/checkout-fields';
import {
    getRestRoute,
    fieldsToJson,
    setFieldValue,
    getErrorMessage,
    isNextActionRequired,
} from '@wc-stripe/utils';

/**
 * Base Controller
 *
 * Orchestrates WooCommerce integration and payment flow.
 * Works with any payment gateway through dependency injection.
 *
 * @since 4.0.0
 */
class BaseController {

    stripe;

    promises = [];

    _isPaymentMethodAvailable = true;

    static attributionDataStored = false;

    constructor({context, gateway, selector = null}) {
        this.cart = cart;
        this.gateway = gateway;
        this.context = context;
        this.selector = selector;
        this.actions = actions;
        this.fields = new CheckoutFields(context, this);
        this.gateway.setController(this);
        this.initActions();
        this.gateway.initialize();
    }

    initActions() {

    }

    initialize() {
        // Initialize payment elements
        this.loadStripeSDK().then(async () => {
            await this.createPaymentElement();
        }).catch((error) => {
            console.log(error);
        });
    }

    async loadStripeSDK() {
        try {
            const stripe = await loadStripeSDK();
            this.stripe = stripe;
            this.actions.doAction('stripe_sdk_loaded', stripe, this);
        } catch (error) {
            console.log(`Error loading Stripe SDK. ${error}`);
            throw error;
        }
    }

    /**
     * Create payment element(s) using the gateway
     */
    async createPaymentElement() {
        // Early return if not available in cart
        if (!this.isPaymentMethodAvailable()) {
            this._isPaymentMethodAvailable = false;
            this.onPaymentUnavailable();
            return;
        }

        this.gateway.onPaymentMethodAvailable();

        // Check if gateway can make payments
        let canMakePayment = false;

        try {
            canMakePayment = await this.gateway.canMakePayment();
        } catch (error) {
            canMakePayment = false;
            console.error('Payment method availability check failed:', error);
        }

        if (!canMakePayment) {
            this._isPaymentMethodAvailable = false;
            this.onPaymentUnavailable();
            return;
        }

        // Reset availability flag if payment method becomes available again
        this._isPaymentMethodAvailable = true;

        const container = this.getElementContainer();
        if (!container) {
            return;
        }

        if (this.gateway.shouldMountElement(container)) {
            if (container instanceof NodeList) {
                for (let i = 0; i < container.length; i++) {
                    const node = container[i];
                    await this.gateway.mountPaymentElement(node);
                }
            } else {
                await this.gateway.mountPaymentElement(container);
            }
            this.$paymentElement = $(container);

            // Called after payment element is created and ready
            this.onPaymentElementReady();
        } else {
            // the element didn't need to be created, but call onPaymentElementReady
            // in case necessary classes or UX logic needs to be called.
            this.onPaymentElementReady();
        }
    }

    isPaymentMethodAvailable() {
        return this.cart.isPaymentMethodAvailable(this.gateway.id);
    }

    getPaymentElement() {
        return this.$paymentElement;
    }

    destroyPaymentElement() {
        this.gateway.destroyPaymentElement?.();
    }

    disablePaymentElement(block = true) {
        this.gateway.disablePaymentElement?.();
        if (block && this?.$paymentElement) {
            this.$paymentElement.addClass('disabled');
        }
    }

    enablePaymentElement() {
        this.gateway.enablePaymentElement?.();
        if (this.$paymentElement) {
            this.$paymentElement.removeClass('disabled');
        }
    }

    displayPaymentElement() {
        this.getPaymentElement()?.show();
    }

    hidePaymentElement() {
        this.getPaymentElement()?.hide();
    }

    needsShipping() {
        return this.cart.needsShipping();
    }

    getElementContainer() {
        let el = document.querySelector(this.selector);
        return this.actions.applyFilters('get_element_container', el, this);
    }

    /**
     * Called when payment method is unavailable
     * Override in subclasses for context-specific unavailable handling
     */
    onPaymentUnavailable() {
        this.actions.doAction('payment_unavailable', this);
    }

    /**
     * Called after payment element is created and ready
     * Override in subclasses for context-specific initialization
     */
    onPaymentElementReady() {
        // Override in subclasses if needed
        this.actions.doAction('payment_element_ready', this);
    }

    /**
     * Called by gateway when payment is cancelled
     */
    onCancel() {
        this.enablePaymentElement();
    }

    /**
     * Called by gateway when an error occurs
     */
    onError(error, source) {
        if (this.currentError) {
            this.submitError(this.currentError);
            this.currentError = null;
        } else {
            this.submitError(error);
        }
        this.enablePaymentElement();
        $(document.body).triggerHandler('wc_stripe_on_error', [this, source, error]);
    }

    async onShippingChange(data) {
        try {
            if (this.promises.length) {
                await Promise.all(this.promises);
                this.promises = [];
            }
            return this.cart.updateShipping({
                payment_method: this.gateway.id,
                ...data
            });
        } catch (error) {
            throw error;
        }
    }

    onBillingChange(data) {
        return this.cart.updateBilling({
            payment_method: this.gateway.id,
            ...data
        }).then(response => {
            return response;
        }).catch(error => {
            throw error;
        })
    }

    submitError(error) {
        this.displayErrorMessage(error, 'div.woocommerce-notices-wrapper');
    }

    /**
     * Display error message to user
     * @param {string|array} error - Error message or array of messages
     * @param {string} selector - jQuery selector for error container
     */
    displayErrorMessage(error, selector = '') {
        error = getErrorMessage(error);
        let $container;
        selector = this.actions.applyFilters(`get_error_message_selector`, selector, this, this.gateway.id);
        if (!selector) {
            $container = $(this.selector).closest('form');
        } else {
            $container = $(selector);
        }
        if ($container.length) {
            $('.woocommerce-NoticeGroup-checkout, .woocommerce-error, .woocommerce-message, .is-error, .is-success')
                .remove();

            const listItems = Array.isArray(error)
                ? error.map(msg => `<li>${msg}</li>`).join('')
                : `<li>${error}</li>`;

            $container.prepend(
                `<div class="woocommerce-NoticeGroup ${this.context.getPage()}">
                <ul class="woocommerce-error" role="alert">${listItems}</ul>
                </div>`
            );
            $.scroll_to_notices?.($container);
        }
    }

    getShippingPrefix() {
        return 'shipping';
    }

    /**
     * Called when payment button is clicked
     * Override in subclasses for context-specific click behavior
     */
    onClick() {
        this.storeAttributionValues();
    }

    /**
     * Perform validations
     * @return Promise
     */
    async validate() {
        return true;
    }

    /**
     * Submit payment after authorization.
     *
     * Default implementation processes cart checkout via REST API.
     * Handles 3DS authentication inline if required by checking the redirect URL.
     *
     * Override in subclasses for context-specific submission logic:
     * - CheckoutController: Submits WooCommerce checkout form
     * - CartController: Uses default (cart checkout via REST API)
     *
     * @param {Object} checkoutData - Optional standardized checkout data to populate fields before submission
     */
    async submit(checkoutData = null) {
        if (checkoutData) {
            this.populateCheckoutFields(checkoutData);
        }

        this.showProcessing();

        try {
            const response = await apiFetch({
                method: 'POST',
                url: getRestRoute('cart/checkout'),
                data: this.getCartCheckoutData()
            });

            if (response.result && 'success' === response.result) {
                // Check if 3DS authentication is required
                const args = isNextActionRequired(response.redirect);

                if (args) {
                    // Handle 3DS inline instead of redirecting
                    const result = await this.handleIntentConfirmation(args);
                    window.location.href = this.getPostAuthenticationRedirectUrl(args, result);
                } else {
                    // Normal success redirect
                    window.location = response.redirect;
                }
            } else {
                // Handle error response
                if (response.messages) {
                    this.submitError(response.messages);
                }
            }
        } catch (error) {
            this.submitError(error);
        } finally {
            this.hideProcessing();
        }
    }

    /**
     * Handle 3DS authentication and intent confirmation.
     *
     * Called when server response indicates 3DS is required or confirmation is needed.
     * Uses Stripe SDK to confirm payment/setup with 3DS inline modal.
     * Returns the result - caller decides what to do next (redirect or submit form).
     *
     * @param {Object} data - 3DS data containing client_secret, type, return_url
     * @param {Object} options - Options to pass to confirmPayment/confirmSetup
     * @param {boolean} options.skipElements - Skip elements in confirmation (for saved payment methods)
     * @returns {Object} The confirmation result containing paymentIntent or setupIntent
     * @throws {Error} If 3DS confirmation fails
     */
    async handleIntentConfirmation(data, options = {}) {
        // Default to skipElements unless the intent requires_payment_method
        const skipElements = data.status !== 'requires_payment_method';
        const confirmOptions = {skipElements, ...options};

        let result;

        // Confirm with Stripe based on intent type
        if (data.type === 'payment_intent') {
            result = await this.gateway.confirmPayment(data, confirmOptions);
        } else if (data.type === 'setup_intent') {
            result = await this.gateway.confirmSetup(data, confirmOptions);
        }

        if (result.error) {
            throw result.error;
        }

        // With redirect: 'if_required', Stripe returns the result instead of redirecting.
        // We need to handle statuses that indicate the payment did not succeed.
        if (data.type === 'payment_intent') {
            const status = result.paymentIntent?.status;
            if (status === 'requires_action' && this.gateway.isPaymentCancelled(result)) {
                throw {code: 'payment_cancelled'};
            }
            if (status === 'requires_payment_method') {
                // Payment method was declined or failed — surface the underlying error code
                throw {
                    code: result.paymentIntent?.last_payment_error?.code,
                    message: result.paymentIntent?.last_payment_error?.message
                };
            }
        } else if (data.type === 'setup_intent') {
            const status = result.setupIntent?.status;
            if (status === 'requires_action') {
                throw {code: 'payment_cancelled'};
            }
            if (status === 'requires_payment_method') {
                // Setup intents use last_setup_error, not last_payment_error
                throw {
                    code: result.setupIntent?.last_setup_error?.code,
                    message: result.setupIntent?.last_setup_error?.message
                };
            }
        }

        return result;
    }

    /**
     * Build redirect URL with intent parameters after 3DS authentication
     *
     * @param {Object} data - Original 3DS data containing return_url and type
     * @param {Object} result - The confirmation result from handleIntentConfirmation
     * @returns {string} The redirect URL with intent parameters
     */
    getPostAuthenticationRedirectUrl(data, result) {
        // Redirect-based payment methods (Klarna, etc.) are normally sent to next_action.redirect_to_url
        // by Stripe.js itself before this ever runs. If that didn't happen - e.g. an ad/privacy blocker
        // interfered - the intent is still sitting in requires_action with that URL attached. Follow it
        // directly rather than sending the browser to our own return_url, which would leave the order
        // pending with no error shown.
        if (data.type === 'payment_intent') {
            const nextAction = result.paymentIntent?.next_action;
            if (result.paymentIntent?.status === 'requires_action' && nextAction?.type === 'redirect_to_url' && nextAction.redirect_to_url?.url) {
                return nextAction.redirect_to_url.url;
            }
        }

        const url = new URL(decodeURI(data.return_url));
        if (data.type === 'payment_intent') {
            url.searchParams.set('payment_intent', result.paymentIntent.id);
            url.searchParams.set('payment_intent_client_secret', result.paymentIntent.client_secret);
        } else {
            url.searchParams.set('setup_intent', result.setupIntent.id);
            url.searchParams.set('setup_intent_client_secret', result.setupIntent.client_secret);
        }
        return url.toString();
    }

    getCartCheckoutData() {
        return {payment_method: this.gateway.id, context: this.context.getPage(), ...fieldsToJson()};
    }

    getProcessingSelector() {
        return 'body';
    }

    showProcessing() {
        $(this.getProcessingSelector())?.block({
            message: null,//this.getProcessingMessage(),
            overlayCSS: {
                background: '#fff',
                opacity: 0.6
            }
        });
    }

    hideProcessing() {
        $(this.getProcessingSelector())?.unblock();
    }

    getProcessingMessage() {
        return `<div class="wc-stripe-loader">
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>`;
    }

    isOrderAttributionEnabled() {
        return typeof wc_order_attribution !== 'undefined' && document.querySelector('wc-order-attribution-inputs') !== null;
    }

    storeAttributionValues() {
        if (BaseController.attributionDataStored) {
            return;
        }
        if (this.context.isCheckout() || !this.isOrderAttributionEnabled()) {
            return;
        }
        const prefix = wc_order_attribution.params.prefix;

        // Store the order attribution values so they will be used in the request.
        $('input[type="hidden"][name^="' + prefix + '"]').each((idx, el) => {
            const $input = $(el);
            const name = $input.attr('name');
            // @todo - add storage of value to field
            setFieldValue(name, $input.val());
        });

        BaseController.attributionDataStored = true;
    }

    /**
     * Get line items for the current context (cart, product, or order)
     * Returns generic line items that can be mapped to wallet-specific formats
     * @returns {Array} Line items in generic format
     */
    getLineItems() {
        return this.cart.getLineItems();
    }

    /**
     * Get shipping options for the current context
     * Returns generic shipping options that can be mapped to wallet-specific formats
     * @returns {Array} Shipping options in generic format
     */
    getShippingOptions() {
        return this.cart.getShippingOptions();
    }

    /**
     * Get the selected shipping method ID
     * @returns {string} Selected shipping method ID
     */
    getSelectedShippingMethod() {
        return this.cart.getSelectedShippingMethod();
    }

    getCurrency() {
        return this.cart.getCurrency();
    }

    /**
     * Get the total amount for the current context
     * @returns {string} Total amount as a string
     */
    getTotal(type = 'string') {
        return this.cart.getTotal();
    }

    /**
     * @returns {Number} Total amount in cents
     */
    getTotalCents() {
        return this.cart.getTotalCents();
    }

    getSubtotalCents() {
        return this.cart.getSubtotalCents();
    }

    needsPayment() {
        return this.cart.needsPayment();
    }

    populateCheckoutFields(data, options = {}) {
        // Delegate to field populator helper
        options = this.actions.applyFilters(
            'field_population_options',
            {
                skipShipping: false,
                needsShipping: this.needsShipping(),
                ...options
            },
            this
        );

        this.fields.populate(data, options);

        this.actions.doAction('after_populate_checkout_fields', this);
    }

    onPaymentRequiresAction(data) {

    }

}

export {BaseController};
export default BaseController;